import { IPlugin } from "@/types";

// Shared widget search: token matching plus synonyms, so "search bar" finds
// Website Search. Used by the homepage search suggestions (DEV-14926) and meant
// for the /widgets library redesign (DEV-15168) too, so both rank alike.

export interface ISearchableWidget {
  name: string;
  href: string;
  order: number;
  teaser: string;
  logo: string;
  categories: string[];
}

// Words people type that do not appear in any widget name (from real
// /widgets?search= queries in GA4), mapped to words that do.
const SYNONYMS: Record<string, string[]> = {
  "search bar": ["website search"],
  searchbar: ["website search"],
  "pop up": ["popup"],
  "pop-up": ["popup"],
  hero: ["banner", "slider"],
  testimonial: ["testimonials", "reviews"],
  booking: ["appointment"],
  schedule: ["appointment", "calendar"],
  donate: ["donation"],
  timer: ["countdown"],
  carousel: ["slider", "carousel"],
  menu: ["restaurant menu"],
  payment: ["paypal", "payment"],
};

function tokenize(text: string) {
  return text.toLowerCase().split(/[^a-z0-9&]+/).filter((t) => t.length > 1);
}

function expandQuery(query: string) {
  const q = query.trim().toLowerCase();
  const extra = Object.entries(SYNONYMS)
    .filter(([k]) => q.includes(k))
    .flatMap(([, v]) => v);
  return Array.from(new Set([...tokenize(q), ...extra.flatMap(tokenize)]));
}

// Every query word scores on its own, so "search bar" still finds
// "Website Search". Name matches weigh most, then category, then description.
function scoreWidget(widget: ISearchableWidget, words: string[]) {
  const name = widget.name.toLowerCase();
  const teaser = widget.teaser.toLowerCase();
  const cats = widget.categories.join(" ").toLowerCase();
  let score = 0;
  words.forEach((w) => {
    if (name.includes(w)) score += 4;
    if (cats.includes(w)) score += 2;
    if (teaser.includes(w)) score += 1;
  });
  return score;
}

export function searchWidgets<T extends ISearchableWidget>(
  widgets: T[],
  query: string
): T[] {
  const words = expandQuery(query);
  if (!words.length) return widgets;
  return widgets
    .map((widget) => ({ widget, score: scoreWidget(widget, words) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.widget.order - b.widget.order)
    .map(({ widget }) => widget);
}

// Only the fields the search and the suggestion rows read, so a page that
// passes this to a client component does not ship whole Mongo documents.
export function toSearchableWidgets(apps: IPlugin[]): ISearchableWidget[] {
  return apps
    .map((app) => ({
      name: app.name,
      href: `/widgets/${app.seoSlug || app.slug}`,
      order: app.order ?? 9999,
      teaser: app.description || app.teaser || "",
      logo: app.logo,
      categories: (app.newCategories || []).map((c) => c.name).filter(Boolean),
    }))
    .sort((a, b) => a.order - b.order);
}
